import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ButtonGroup from "components/buttonGroup/ButtonGroup";
import { CustomColorPickerPopover } from "components/colorpicker";
import "./colorsView.scss";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { clone } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { IWebsiteBuilderWebsiteVersionGlobalStyles } from "redux/reducers/models/websiteBuilder";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { websiteBuilderUpdateGlobalStyles } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { IWebsiteBuilderToolbarMenuViewSection, WebsiteBuilderToolbarMenuView } from "../../../WebsiteBuilderToolbarMenuView";

interface IColorsViewProps {
  goBackToParentView: () => void;
}

export function ColorsView(props: IColorsViewProps) {
  const { goBackToParentView } = props;

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const globalStyles = useAppSelector(store => store.websiteBuilderStore.editor.globalStyles);

  const [updatedGlobalStyles, setUpdatedGlobalStyles] = useState<IWebsiteBuilderWebsiteVersionGlobalStyles>(globalStyles != null ? clone(globalStyles) : null);

  function dispatchGlobalStyles(
    prevGlobalStyles: IWebsiteBuilderWebsiteVersionGlobalStyles,
    nextGlobalStyles: IWebsiteBuilderWebsiteVersionGlobalStyles,
  ) {
    dispatch(
      websiteBuilderUpdateGlobalStyles({
        globalStyles: clone(nextGlobalStyles),
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: globalStyles,
    updatedState: updatedGlobalStyles,
    setUpdatedState: setUpdatedGlobalStyles,
    dispatchState: dispatchGlobalStyles,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.SHORT,
  });

  function renderTextColorsSection() {
    return (
      <>
        <CustomColorPickerPopover
          title={t("Headings")}
          hexColor={updatedGlobalStyles.heading_color}
          updateHexColor={heading_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              heading_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <CustomColorPickerPopover
          title={t("Body Text")}
          hexColor={updatedGlobalStyles.body_text_color}
          updateHexColor={body_text_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              body_text_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
      </>
    );
  }

  function renderButtonColorsSection() {
    return (
      <>
        <CustomColorPickerPopover
          title={t("Button text")}
          hexColor={updatedGlobalStyles.button_color}
          updateHexColor={button_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              button_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <CustomColorPickerPopover
          title={t("Button background")}
          hexColor={updatedGlobalStyles.button_background_color}
          updateHexColor={button_background_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              button_background_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <CustomColorPickerPopover
          title={t("Button border")}
          hexColor={updatedGlobalStyles.button_border_color}
          updateHexColor={button_border_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              button_border_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
      </>
    );
  }

  function renderButtonHoverSection() {
    return (
      <>
        <div className="website-builder-hover-change">
          <label>{t("Hover change")}</label>
          <ButtonGroup
            fullWidth
            startKey={updatedGlobalStyles.button_hover_transition === "smooth" ? "smooth" : "instant"}
            buttons={[
              { key: "instant", label: t("Instant") },
              { key: "smooth", label: t("Smooth") },
            ]}
            onButtonClick={buttonHoverTransition =>
              handleUpdateState(prev => ({
                ...prev,
                button_hover_transition: String(buttonHoverTransition),
              }))
            }
          />
        </div>
        <CustomColorPickerPopover
          title={t("Button text")}
          hexColor={updatedGlobalStyles.button_hover_color}
          updateHexColor={button_hover_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              button_hover_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <CustomColorPickerPopover
          title={t("Button background")}
          hexColor={updatedGlobalStyles.button_hover_background_color}
          updateHexColor={button_hover_background_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              button_hover_background_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <CustomColorPickerPopover
          title={t("Button border")}
          hexColor={updatedGlobalStyles.button_hover_border_color}
          updateHexColor={button_hover_border_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              button_hover_border_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
      </>
    );
  }

  function renderLinkColorsSection() {
    return (
      <>
        <CustomColorPickerPopover
          title={t("Link")}
          hexColor={updatedGlobalStyles.link_color}
          updateHexColor={link_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              link_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <CustomColorPickerPopover
          title={t("Link hover")}
          hexColor={updatedGlobalStyles.link_hover_color}
          updateHexColor={link_hover_color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              link_hover_color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
      </>
    );
  }

  const sections: IWebsiteBuilderToolbarMenuViewSection[] = updatedGlobalStyles ? [
    {
      title: t("Text"),
      content: renderTextColorsSection(),
    },
    {
      title: t("Buttons"),
      content: renderButtonColorsSection(),
    },
    {
      title: t("Button hover"),
      content: renderButtonHoverSection(),
    },
    {
      title: t("Links"),
      content: renderLinkColorsSection(),
    },
  ] : [];

  return (
    <WebsiteBuilderToolbarMenuView
      title={t("Colors")}
      goBackToParentView={goBackToParentView}
      sections={sections}
    />
  );
}
